import { z } from "zod";
import {
  MANUAL_COMMISSION_KINDS,
  MAX_TITLE_CENTS,
  isIsoDate,
  periodProblem,
  type CommissionBase,
  type CommissionEntryKind,
  type CommissionStatementSummary,
} from "@cx/domain";
import { listQuery } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Conta-corrente de comissões (T1.11, RF-VEN-08) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const positive = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(1, `Informe ${what}`)
    .max(MAX_TITLE_CENTS, "Valor acima do limite");

/** Período: os dois ou nenhum (ausente = mês atual em America/Sao_Paulo), até 366 dias. */
const period = {
  from: businessDate.optional(),
  to: businessDate.optional(),
};
const refinePeriod = (v: { from?: string; to?: string }, ctx: z.RefinementCtx) => {
  if (v.from === undefined && v.to === undefined) return;
  if (v.from === undefined)
    return ctx.addIssue({ code: "custom", path: ["from"], message: "Informe o início" });
  if (v.to === undefined) return ctx.addIssue({ code: "custom", path: ["to"], message: "Informe o fim" });
  // Data que já falhou no formato não ganha um segundo erro.
  if (!isIsoDate(v.from) || !isIsoDate(v.to)) return;
  const p = periodProblem(v.from, v.to);
  if (p) ctx.addIssue({ code: "custom", path: ["to"], message: p });
};

export const commissionSummaryQuery = z
  .object({
    ...period,
    /** "Listar vendedores sem movimentação" (PRD). */
    includeIdle: z.stringbool({ error: "Use true ou false" }).default(false),
  })
  .superRefine(refinePeriod);
export type CommissionSummaryQuery = z.output<typeof commissionSummaryQuery>;

export const commissionStatementQuery = listQuery.omit({ q: true }).extend(period).superRefine(refinePeriod);
export type CommissionStatementQuery = z.output<typeof commissionStatementQuery>;

/** Outro crédito ou outro débito, com motivo (vai para o extrato). */
export const commissionEntryInput = z.object({
  sellerId: ulidParam,
  kind: z.enum(MANUAL_COMMISSION_KINDS, "Escolha crédito ou débito"),
  amountCents: positive("o valor"),
  date: businessDate,
  notes: z.string("Informe o motivo").trim().min(1, "Informe o motivo").max(500, "Até 500 caracteres"),
});
export type CommissionEntryInput = z.output<typeof commissionEntryInput>;
export type CommissionEntryFormValues = z.input<typeof commissionEntryInput>;

export const commissionReverseInput = z.object({ notes: text(200) });
export type CommissionReverseInput = z.output<typeof commissionReverseInput>;

/** Pagamento: lança no extrato e gera a conta a pagar para o vendedor (vencimento, categoria e forma da conta). */
export const commissionPaymentInput = z.object({
  sellerId: ulidParam,
  amountCents: positive("o valor"),
  date: businessDate,
  dueDate: businessDate,
  financialCategoryId: nullish(ulidParam),
  paymentMethodId: nullish(ulidParam),
  notes: text(500),
});
export type CommissionPaymentInput = z.output<typeof commissionPaymentInput>;
export type CommissionPaymentFormValues = z.input<typeof commissionPaymentInput>;

export type CommissionPeriodSummary = CommissionStatementSummary & {
  /** Faturado no período: base de comissão dos pedidos com contas lançadas (ativos). */
  invoicedCents: number | null;
  /** (*) Comissões pendentes: ainda não liberadas (base recebido), informativo. */
  pendingCents: number | null;
};

export type CommissionSellerSummary = CommissionPeriodSummary & {
  sellerId: string;
  sellerName: string;
  base: CommissionBase;
  /** Vendedor excluído (aparece se tiver saldo ou movimento). */
  deleted: boolean;
};

export type CommissionSummaryResult = {
  from: string;
  to: string;
  data: CommissionSellerSummary[];
};

export type CommissionEntryView = {
  id: string;
  date: string;
  kind: CommissionEntryKind;
  /** Com sinal: efeito no saldo do vendedor. */
  amountCents: number;
  /** Base de venda da comissão (pedido), quando houver. */
  baseCents: number | null;
  orderId: string | null;
  orderNumber: number | null;
  settlementId: string | null;
  /** Conta a pagar do pagamento. */
  titleId: string | null;
  reversalOfId: string | null;
  reversedAt: string | null;
  notes: string | null;
  createdAt: string;
};

export type CommissionPendingView = {
  orderId: string;
  orderNumber: number | null;
  postedOn: string;
  commissionCents: number;
  releasedCents: number;
  pendingCents: number;
};

export type CommissionStatement = {
  seller: { id: string; name: string; base: CommissionBase; deleted: boolean };
  from: string;
  to: string;
  summary: CommissionPeriodSummary & { balanceCents: number | null };
  /** Lançamentos do período, mais recentes primeiro. */
  data: CommissionEntryView[];
  nextCursor: string | null;
  /** Pedidos com comissão a liberar (até 100, os mais antigos primeiro). */
  pending: CommissionPendingView[];
};

export type CommissionPaymentOptions = {
  categories: { id: string; path: string; active: boolean }[];
  paymentMethods: { id: string; name: string; active: boolean }[];
};

export type CommissionEntryCreated = { id: string };
export type CommissionPaymentCreated = { id: string; titleId: string };
